// Mutators for moving/reordering layers and folders within a map, between folders, or between
// maps - both the single-item context-menu actions and the bulk operations used by the layer
// list (see `../features/map-layer-list/reorder-utils.ts` for the SEPARATE flat-list drag-and-drop
// system, which goes through `createReorderFlatLayers` instead of the position-based mutator here).

import { MoveDirection } from "@nens/shared-utils";

import { rebuildLayersFromOrder } from "../features/map-layer-list/utils/reorder-utils";
import { Folder, Layer, PublicationMap } from "../types/publication";

import { getMaps, getOpenMap } from "./selectors";
import { GetFunction } from "./store";

/**
 * Helper: Extract layers (and, at the root level, folders) by IDs, handling nested removal.
 * Folders are only ever found at the root level - a folder's own contents only ever contain
 * layers, so recursing into a folder only extracts layers from it.
 */
function extractLayersById(mapLayers: PublicationMap["layers"], layerIds: string[]) {
  const idSet = new Set(layerIds);
  const extracted: Array<Layer | Folder> = [];
  const filtered: PublicationMap["layers"] = [];

  for (const item of mapLayers) {
    if (idSet.has(item.id)) {
      extracted.push(item);
    } else if (item.type === "folder") {
      // Folder: recursively check sublayers
      const { extracted: subExtracted, remaining } = extractLayersFromFolder(item, idSet);
      extracted.push(...subExtracted);
      // Keep the folder even if it becomes empty - folders can be empty.
      filtered.push(remaining);
    } else {
      filtered.push(item);
    }
  }

  return { extracted, filtered };
}

function extractLayersFromFolder(folder: Folder, idSet: Set<string>) {
  const extracted: Layer[] = [];
  const remaining: Layer[] = [];

  for (const layer of folder.layers) {
    if (layer.type === "layer" && idSet.has(layer.id)) {
      extracted.push(layer);
    } else if (layer.type === "layer") {
      remaining.push(layer);
    }
  }

  return { extracted, remaining: { ...folder, layers: remaining } };
}

/**
 * Counts how many entries of `container` (an array as it was BEFORE any extraction) that sit
 * before `targetIndex` are being removed (i.e. their id is in idSet). This is exactly how much
 * the insertion index needs to shift left once those entries are actually removed, so that the
 * drop position visually matches where the drop line was rendered.
 */
function countRemovedBefore(
  container: Array<{ id: string }>,
  idSet: Set<string>,
  targetIndex: number
): number {
  let count = 0;
  for (let i = 0; i < targetIndex && i < container.length; i++) {
    if (idSet.has(container[i].id)) count++;
  }
  return count;
}

/**
 * Works out the index (in `container`, as it was BEFORE any extraction) that `ids` should be
 * reinserted at for a given direction: "top"/"bottom" send them to either end, "up"/"down" shift
 * the whole (possibly multi-item) selection one step past its nearest neighbour on that side,
 * based on where the selection currently sits in `container`. Mirrors `moveUp`/`moveDown`'s
 * single-item adjacent-swap semantics, generalized to a block of items.
 */
function getDirectionalTargetIndex(
  container: Array<{ id: string }>,
  ids: string[],
  direction: MoveDirection
): number {
  if (direction === "top") return 0;
  if (direction === "bottom") return container.length;

  const idSet = new Set(ids);
  const indices = container.reduce<number[]>((acc, item, index) => {
    if (idSet.has(item.id)) acc.push(index);
    return acc;
  }, []);
  if (indices.length === 0) return direction === "up" ? 0 : container.length;

  // "up": land right before the item preceding the selection. "down": land right after the item
  // following the selection (+2, since the selection's own removal shifts everything after it).
  return direction === "up" ? indices[0] - 1 : indices[indices.length - 1] + 2;
}

/**
 * Move one or more layers (and/or folders, when moving at the root level) up/down/to the
 * top/bottom of the map or a folder. `direction` is relative to whichever list `layerIds`
 * currently sit in - the target folder's children if `targetFolderId` is given, otherwise the
 * root list - so this covers both single-item context-menu moves and bulk moves.
 */
export const createMoveLayersToPosition =
  (get: GetFunction) =>
  (_layerIds: string[], direction: MoveDirection, targetFolderId?: string) => {
    const openMap = getOpenMap(get());

    // Folders can't be moved into another folder - drop any folder ids from the selection when
    // the target is a folder, leaving those folders untouched at their current position.
    let layerIds = _layerIds;
    if (targetFolderId) {
      const layerIdSet = new Set(_layerIds);
      const hasFolderSelected = openMap.layers.some(
        (item) => item.type === "folder" && layerIdSet.has(item.id)
      );
      if (hasFolderSelected) {
        layerIds = _layerIds.filter((id) => {
          const isFolder = openMap.layers.some((item) => item.type === "folder" && item.id === id);
          return !isFolder;
        });
      }
    }

    if (layerIds.length === 0) return;

    const container = targetFolderId
      ? openMap.layers.find(
          (layer): layer is Folder => layer.type === "folder" && layer.id === targetFolderId
        )?.layers
      : openMap.layers;
    if (!container) return;

    const targetIndex = getDirectionalTargetIndex(container, layerIds, direction);

    const idSet = new Set(layerIds);
    const { extracted, filtered } = extractLayersById(openMap.layers, layerIds);

    if (extracted.length === 0) return;

    if (targetFolderId) {
      // Moving into a folder
      const folderIndex = filtered.findIndex(
        (layer): layer is Folder => layer.type === "folder" && layer.id === targetFolderId
      );
      if (folderIndex === -1) return;

      const folder = filtered[folderIndex] as Folder;
      const originalFolder = openMap.layers.find(
        (layer): layer is Folder => layer.type === "folder" && layer.id === targetFolderId
      );
      // Adjust the target index for any dragged items that originally sat before it within
      // this same folder, since removing them shifts everything after them left.
      const removedBefore = originalFolder
        ? countRemovedBefore(originalFolder.layers, idSet, targetIndex)
        : 0;
      const insertIndex = Math.max(0, Math.min(targetIndex - removedBefore, folder.layers.length));

      const newFolderLayers = [
        ...folder.layers.slice(0, insertIndex),
        ...(extracted as Layer[]),
        ...folder.layers.slice(insertIndex),
      ];
      const updatedFolder: Folder = {
        ...folder,
        layers: newFolderLayers,
      };
      const newLayers = [
        ...filtered.slice(0, folderIndex),
        updatedFolder,
        ...filtered.slice(folderIndex + 1),
      ];
      get().replaceMap({
        ...openMap,
        layers: newLayers,
      });
    } else {
      // Moving at the root level. Adjust the target index for any dragged items that originally
      // sat before it at the root, since removing them shifts everything after them left. Items
      // extracted out of a folder don't affect root-level indices, so they're not counted here.
      const removedBefore = countRemovedBefore(openMap.layers, idSet, targetIndex);
      const insertIndex = Math.max(0, Math.min(targetIndex - removedBefore, filtered.length));
      const newLayers = [
        ...filtered.slice(0, insertIndex),
        ...extracted,
        ...filtered.slice(insertIndex),
      ];
      get().replaceMap({
        ...openMap,
        layers: newLayers,
      });
    }
  };

/**
 * Commit a full drag reorder of the flat, top-to-bottom visual layer list (root layers/folders,
 * plus every open folder's children) in one go. `order` is the reconstructed `{ id, folderId? }`
 * list produced by `reconstructOrder` (see `reorder-utils.ts`) from the single root
 * `Reorder.Group`'s `onReorder` callback - every visible id, in its new order, together with which
 * folder (if any) it now belongs to. This rebuilds the whole layer tree from that flat list in one
 * pass, rather than doing incremental index math like `createMoveLayersToPosition`.
 *
 * `expandedFolderIds` is the same folder-id -> opened map used to build `order` (see
 * `reconstructOrder`), reflecting whether each folder was actually expanded as part of THIS
 * specific flat traversal - which can differ from the folder's real `opened` state, e.g. while a
 * folder's own header is being dragged it's rendered/traversed as collapsed even though it's
 * really open. A folder that wasn't expanded for this traversal never had its children walked at
 * all, so its `layers` are left completely untouched here rather than being wiped to `[]`.
 */
export const createReorderFlatLayers =
  (get: GetFunction) =>
  (order: Array<{ id: string; folderId?: string }>, expandedFolderIds: Map<string, boolean>) => {
    const openMap = getOpenMap(get());
    get().replaceMap({
      ...openMap,
      layers: rebuildLayersFromOrder(openMap.layers, order, expandedFolderIds),
    });
  };

/**
 * Move multiple layers to another map
 */
export const createMoveLayersToMap = (get: GetFunction) => (layerIds: string[], mapId: string) => {
  const { extracted, filtered } = extractLayersById(getOpenMap(get()).layers, layerIds);

  if (extracted.length === 0) return;

  const maps = getMaps(get());
  const targetMap = maps.find((map) => map.id === mapId);

  if (targetMap) {
    // Update current map (remove layers)
    get().replaceMap({
      ...getOpenMap(get()),
      layers: filtered,
    });

    // Update target map (add layers)
    get().replaceMap({
      ...targetMap,
      layers: [...targetMap.layers, ...extracted],
    });
  }
};

/**
 * Add multiple layers to one or more folders
 */
export const createAddLayersToFolder =
  (get: GetFunction) => (folderIds: string[], layerIds: string[]) => {
    const openMap = getOpenMap(get());
    const { extracted } = extractLayersById(openMap.layers, layerIds);

    if (extracted.length === 0) return;

    // Remove from top level
    const { filtered } = extractLayersById(openMap.layers, layerIds);
    get().replaceMap({
      ...openMap,
      layers: filtered,
    });

    // Add to each folder
    for (const folderId of folderIds) {
      const folder = openMap.layers.find(
        (layer): layer is Folder => layer.type === "folder" && layer.id === folderId
      );
      if (folder) {
        get().replaceLayer({
          ...folder,
          layers: [...folder.layers, ...extracted],
        });
      }
    }
  };
